/**
 * 开发环境首页固定项目守卫
 *
 * 当进入首页 `home` 路由且满足下列全部条件时，直接跳转到配置的固定项目编辑器：
 *   1. 用户已登录
 *   2. `apiConfig.devAuth.autoOpenProjectId` 非空
 *
 * 不满足任一条件时放行原导航。
 *
 * 约束：
 * - 只对首页语义入口生效，避免对 editor / preview 等页面重复改写目标
 * - 使用显式 path 拼接项目 URL，确保后端项目路径中的空格与 `/` 被稳定编码
 * - 不参与登录请求，不持有认证状态
 */

import type { NavigationGuardWithThis, RouteLocationNormalized, NavigationGuardNext } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { apiConfig } from '@/services/api/config';
import { EDITOR_ENTRY_SOURCE_QUERY_KEY, EditorEntrySource } from '@/router/editor-entry-source';
import { NOT_FOUND_ROUTE_NAME } from '@/router/not-found-external-redirect-guard';
import { AUTH_ERROR_REASON, AUTH_ERROR_REASON_QUERY_KEY } from '@/router/auth-error-reason';
import { buildOpenProjectRouteQuery } from '@/router/open-project-route-context';

export interface DevProjectEntryGuardDeps {
  /**
   * 读取开发环境自动登录开关。
   * 该开关关闭时，首页固定项目直达也必须一起关闭，
   * 避免“旧登录链路成功但首页仍被开发守卫改写”的语义分裂。
   */
  getAutoLoginEnabled: () => boolean;

  /**
   * 读取当前是否已处于认证态。
   */
  getIsAuthenticated: () => boolean;

  /**
   * 读取开发环境自动登录是否已经失败。
   *
   * 该依赖为可选项，便于旧测试和非默认实例只关心“首页直达项目”的基础行为。
   */
  getHasDevelopmentAutoLoginFailure?: () => boolean;

  /**
   * 读取固定项目 ID。
   */
  getAutoOpenProjectId: () => string;

  /**
   * 读取固定目标仓库 ID。
   */
  getAutoOpenRepositoryId?: () => string;

  /**
   * 读取固定目标分支 ID。
   */
  getAutoOpenBranchId?: () => string;
}

/**
 * 判断当前路由是否是开发环境固定项目直达入口。
 *
 * @param to 当前目标路由。
 * @returns 是否应该进入开发环境固定项目直达判断。
 */
function isDevelopmentProjectEntryRoute(to: RouteLocationNormalized): boolean {
  if (to.name === 'home') {
    return true;
  }

  /**
   * 当前路由表已移除实际首页组件，根路径 `/` 会由 catch-all 命中为 not-found。
   * 这里保留根路径的“开发入口”语义，同时继续把其他未知路径交给外部承接守卫。
   */
  return to.name === NOT_FOUND_ROUTE_NAME && to.path === '/';
}

/**
 * 规范化开发环境固定项目 ID。
 *
 * @param rawProjectId 环境变量中读取到的原始项目 ID。
 * @returns 去除首尾空白后的项目 ID；若配置为空白则返回空字符串。
 */
function normalizeDevelopmentProjectId(rawProjectId: string): string {
  /**
   * 环境变量示例里常见 `VITE_DEV_AUTO_OPEN_PROJECT_ID= 'xxx/'` 这类写法，
   * Vite 会去掉外层引号但保留等号后的首尾空格，因此这里必须统一收口。
   */
  return rawProjectId.trim();
}

/**
 * 生成开发环境固定项目 editor 路径。
 *
 * @param projectId 已规范化的项目 ID。
 * @returns 可直接交给 Vue Router 的 editor path。
 */
function buildDevelopmentProjectEditorPath(projectId: string): string {
  /**
   * 项目 ID 对后端是文件路径语义，可能包含空格、中文或尾部 `/`。
   * 这里按单个路由参数整体编码，防止 `/` 被浏览器误解析成路径分隔符。
   */
  return `/projects/${encodeURIComponent(projectId)}/editor`;
}

export function createDevProjectEntryGuard(deps: DevProjectEntryGuardDeps) {
  return (
    to: RouteLocationNormalized,
    _from: RouteLocationNormalized,
    next: NavigationGuardNext,
  ): void => {
    if (!isDevelopmentProjectEntryRoute(to)) {
      next();
      return;
    }
    if (!deps.getAutoLoginEnabled()) {
      next();
      return;
    }
    if (!deps.getIsAuthenticated()) {
      if (deps.getHasDevelopmentAutoLoginFailure?.()) {
        /**
         * 开启开发自动登录后，根路径就是本地开发入口。
         * 自动登录失败时必须停留在 SPA 内给出明确错误，不能继续落到 not-found 外部承接。
         */
        next({
          name: 'auth-error',
          query: {
            [AUTH_ERROR_REASON_QUERY_KEY]: AUTH_ERROR_REASON.DevAutoLoginFailed,
          },
        });
        return;
      }
      next();
      return;
    }
    const projectId = normalizeDevelopmentProjectId(deps.getAutoOpenProjectId());
    if (!projectId) {
      next();
      return;
    }
    //------ 开发直达装配体入口标记 开始------
    next({
      path: buildDevelopmentProjectEditorPath(projectId),
      query: {
        ...buildOpenProjectRouteQuery({
          repositoryId: normalizeDevelopmentProjectId(deps.getAutoOpenRepositoryId?.() ?? ''),
          branchId: normalizeDevelopmentProjectId(deps.getAutoOpenBranchId?.() ?? ''),
        }),
        [EDITOR_ENTRY_SOURCE_QUERY_KEY]: EditorEntrySource.DevAutoOpenProject,
      },
    });
    //------ 开发直达装配体入口标记 结束------
  };
}

/**
 * 默认守卫实例 —— router/index.ts 直接使用
 */
export const devProjectEntryGuard: NavigationGuardWithThis<undefined> = (to, from, next) => {
  const authStore = useAuthStore();
  const guard = createDevProjectEntryGuard({
    getAutoLoginEnabled: () => apiConfig.devAuth.autoLoginEnabled,
    getIsAuthenticated: () => authStore.isAuthenticated,
    getHasDevelopmentAutoLoginFailure: () => authStore.hasDevelopmentAutoLoginFailure,
    getAutoOpenProjectId: () => apiConfig.devAuth.autoOpenProjectId,
    getAutoOpenRepositoryId: () => apiConfig.devAuth.autoOpenRepositoryId,
    getAutoOpenBranchId: () => apiConfig.devAuth.autoOpenBranchId,
  });
  return guard(to, from, next);
};
